import type { FC } from 'react'
import { useState, useEffect } from 'react'
import styled from '@emotion/styled'
import type { AnimationItem } from 'lottie-web'

import SectionBase, * as Section from '@segment/matcha/recipes/Section'
import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'

import useLottie from '@segment/utils/useLottie/useLottie'

import useIsInView from '@segment/utils/useInView'

import jsonUrl from './assets/striking-the-balance-lottie.json.data'

const HEADLINE = `Striking the balance between convenience and privacy.`
const description = (
  <>
    <CopyText.Description tag="p" variant="pLarge">
      While overall knowledge of data security is on the rise, there appears to be a genuine willingness to share data
      safely and securely in exchange for the convenience of personalization.
    </CopyText.Description>
    <CopyText.Description tag="p" variant="pLarge">
      Almost half of consumers say they’re happy to exchange personal data in exchange for personalization.
    </CopyText.Description>
  </>
)

const StrikingTheBalance: FC = () => {
  const [animationData, setAnimationData] = useState<Promise<Response>>()
  useEffect(() => {
    setAnimationData(fetch(jsonUrl))
  }, [])
  const [animation, containerRef] = useLottie<AnimationItem, HTMLDivElement>({
    options: {
      autoplay: false,
      animationData,
      loop: false,
    },
  })
  const isInView = useIsInView(containerRef)

  useEffect(() => {
    if (isInView) {
      animation?.play()
    }
  }, [animation, isInView])

  return (
    <SectionBase variant="2Col" reverse={true}>
      <Section.Contents>
        <Animation ref={containerRef} />
      </Section.Contents>
      <Section.Copy alignSelf="center">
        <CopyTextBase>
          <CopyText.Heading tag="h2" variant="h2">
            {HEADLINE}
          </CopyText.Heading>
          {description}
        </CopyTextBase>
      </Section.Copy>
    </SectionBase>
  )
}

export default StrikingTheBalance

const Animation = styled.div`
  max-width: 447px;
  margin: 0 auto;
  aspect-ratio: 447 / 581;
`
